import React from 'react';
import { Trash2, Warehouse, Star } from 'lucide-react';
import { FilterableSelect } from '../../../../../../components/ui/FilterableSelect';
import { PhoneInput } from '../../../../../../components/ui/PhoneInput';
import { AddressFields } from '../../../../../../components/address/AddressFields';
import { PickupScheduleFields } from '../../../../../../components/warehouses/PickupScheduleFields';
import {
  WAREHOUSE_SHIPPING_METHOD_OPTIONS,
  DEFAULT_WAREHOUSE_CAPACITY,
} from '../../../../../../features/sellers/sellerFormConstants';
import { toWarehousePickupPatch } from '../../../../../../features/sellers/sellerFormUtils';
import { Field, inputClass } from './FormField';


/**
 * One warehouse of the seller being created.
 *
 * Fully controlled: it holds no state of its own and reports every change as a
 * patch, which is also the shape `AddressFields` emits, so the address block
 * can be wired straight through.
 *
 * @param {object} props
 * @param {object} props.warehouse
 * @param {number} props.index
 * @param {Object.<string, string>} [props.errors]
 * @param {string} props.country - ISO2 of the account
 * @param {Array<{value: number, label: string}>} props.zoneOptions
 * @param {boolean} props.loadingZones
 * @param {(patch: object) => void} props.onChange
 * @param {() => void} props.onRemove
 * @param {boolean} props.canRemove
 */
export const WarehouseCard = ({
  warehouse,
  index,
  errors = {},
  country,
  zoneOptions,
  loadingZones,
  onChange,
  onRemove,
  canRemove,
}) => {
  const isFirst = index === 0;
  const hasZones = zoneOptions.length > 0;

  return (
    <div className="rounded-2xl border border-border bg-white dark:bg-[#1a1a1a] overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border bg-ninja-gray/40 dark:bg-[#111]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-ninja-greenLight text-ninja-green flex items-center justify-center shrink-0">
            <Warehouse size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-ninja-dark dark:text-white truncate">
              Bodega {index + 1}
            </h3>
            {isFirst && (
              <p className="flex items-center gap-1 text-xs text-text-secondary">
                <Star size={11} className="text-ninja-green" />
                Queda como bodega por defecto y de devolución
              </p>
            )}
          </div>
        </div>

        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-danger text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 size={15} />
            Eliminar
          </button>
        )}
      </div>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Nombre de la bodega" error={errors.name}>
            <input
              value={warehouse.name}
              onChange={(event) => onChange({ name: event.target.value })}
              maxLength={100}
              placeholder="Ej: Bodega Central"
              className={inputClass}
            />
          </Field>

          <Field label="Nombre del encargado" error={errors.storeKeeper}>
            <input
              value={warehouse.storeKeeper}
              onChange={(event) => onChange({ storeKeeper: event.target.value })}
              maxLength={100}
              className={inputClass}
            />
          </Field>

          <Field label="Teléfono del encargado (opcional)" error={errors.phone}>
            <PhoneInput
              value={warehouse.phone}
              countryIso2={warehouse.phoneCountry}
              onChange={(phone) => onChange({ phone })}
              onCountryChange={(phoneCountry) => onChange({ phoneCountry })}
              hasError={Boolean(errors.phone)}
              name={`warehouse-phone-${index}`}
            />
          </Field>

          <Field label="Correo del encargado (opcional)" error={errors.email}>
            <input
              type="email"
              value={warehouse.email}
              onChange={(event) => onChange({ email: event.target.value })}
              className={inputClass}
            />
          </Field>

          <Field
            label="WhatsApp de contacto"
            error={errors.customerPhone}
            hint="El número que verán los clientes para consultas de sus envíos."
          >
            <PhoneInput
              value={warehouse.customerPhone}
              countryIso2={warehouse.customerPhoneCountry}
              onChange={(customerPhone) => onChange({ customerPhone })}
              onCountryChange={(customerPhoneCountry) => onChange({ customerPhoneCountry })}
              hasError={Boolean(errors.customerPhone)}
              name={`warehouse-customer-phone-${index}`}
            />
          </Field>

          <Field label="Capacidad (opcional)" error={errors.capacity}>
            <input
              type="number"
              min="1"
              value={warehouse.capacity}
              onChange={(event) => onChange({ capacity: event.target.value })}
              placeholder={String(DEFAULT_WAREHOUSE_CAPACITY)}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="h-px bg-border" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <AddressFields
            country={country}
            value={warehouse}
            onChange={onChange}
            errors={errors}
          />

          <Field label="Referencia (opcional)" error={errors.comments}>
            <textarea
              value={warehouse.comments}
              onChange={(event) => onChange({ comments: event.target.value })}
              rows={2}
              maxLength={255}
              placeholder="Ej: local 6, piso -1"
              className={inputClass}
            />
          </Field>
        </div>

        <div className="h-px bg-border" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FilterableSelect
              variant="form"
              label="Zona de despacho"
              value={warehouse.shippingZone}
              onChange={(shippingZone) => onChange({ shippingZone })}
              options={zoneOptions}
              disabled={loadingZones || !hasZones}
              hasError={Boolean(errors.shippingZone)}
              placeholder={
                loadingZones
                  ? 'Cargando...'
                  : hasZones
                    ? 'Seleccionar...'
                    : 'No hay zonas disponibles'
              }
              searchPlaceholder="Buscar zona..."
            />
            {errors.shippingZone && (
              <p className="text-danger text-xs mt-1">{errors.shippingZone}</p>
            )}
          </div>

          <div>
            <FilterableSelect
              variant="form"
              label="Método de envío"
              value={warehouse.shippingMethod}
              onChange={(shippingMethod) => onChange({ shippingMethod })}
              options={WAREHOUSE_SHIPPING_METHOD_OPTIONS}
              hasError={Boolean(errors.shippingMethod)}
              searchPlaceholder="Buscar método..."
            />
            {errors.shippingMethod && (
              <p className="text-danger text-xs mt-1">{errors.shippingMethod}</p>
            )}
          </div>
        </div>

        <div className="h-px bg-border" />

        <PickupScheduleFields
          idPrefix={`warehouse-${index}-pickup`}
          allDay={Boolean(warehouse.allDayPickup)}
          amFrom={warehouse.amPickupFrom}
          amTo={warehouse.amPickupTo}
          pmFrom={warehouse.pmPickupFrom}
          pmTo={warehouse.pmPickupTo}
          onChange={(patch) => onChange(toWarehousePickupPatch(patch))}
          errors={{ am: errors.amPickupWindow, pm: errors.pmPickupWindow }}
        />
      </div>
    </div>
  );
};
